import React, { useCallback, useReducer, createContext, useContext } from 'react';
import debouncePromise from 'debounce-promise';

import {
  getZipcodeLocality,
  searchForCountry,
  getPlaces,
  autocompleteAddresses,
} from 'modules/point-of-interest/service';

import {
  convertPlaceToBBox,
  parseFeaturesForSelect,
  convertPlaceToLatLngArray,
} from 'modules/point-of-interest/utils';

import reducer, {
  INIT_STATE,
  setFormValue,
  setReset,
  setLocalityGID,
  setClearFormValues,
} from './POISearcherReducer';

import { POIMapContext } from './POIMapContext';
import { POIVenuesContext } from './POIVenuesContext';

const POISearcherContext = createContext();

const getZipcodeLocalityDebounced = debouncePromise(getZipcodeLocality, 500, { leading: false });
const autocompleteAddressesDebounced = debouncePromise(autocompleteAddresses, 500, {
  leading: false,
});

function POISearcherContextProvider(props) {
  const { centerMap, createMainMarker, clearMap } = useContext(POIMapContext);
  const { fetchVenues, clearVenues } = useContext(POIVenuesContext);

  const [state, dispatch] = useReducer(reducer, INIT_STATE);

  const { country, zipcode, locality, address, venue, addressInputValue, venueInputValue } =
    state.form;

  const { localityGID } = state;

  const _handleSelectChange = useCallback(
    ({ inputCurrentValue, inputChangeInfo, value, inputValue, field, inputField }) => {
      const { action } = inputChangeInfo;
      if (value && inputValue) {
        dispatch(setFormValue(field, null));
      }
      if (['input-blur', 'menu-close'].includes(action)) {
        dispatch(setFormValue(inputField, inputValue));
        return;
      }
      dispatch(setFormValue(inputField, inputCurrentValue));
    },
    []
  );

  const handleCountryChange = useCallback(
    async (countryValue) => {
      dispatch(setReset());
      clearMap();
      clearVenues();

      dispatch(setFormValue('country', countryValue));

      const countryResponse = await searchForCountry(countryValue.value);
      const bounds = convertPlaceToBBox(countryResponse);

      centerMap(bounds);
    },
    [centerMap, clearMap, clearVenues]
  );

  const handleZipcodeChange = useCallback(
    async (e, { value: zipcodeValue }) => {
      dispatch(setFormValue('zipcode', zipcodeValue));

      dispatch(
        setClearFormValues(['locality', 'address', 'addressInputValue', 'venue', 'venueInputValue'])
      );

      const { locality: newLocality, localityGID: newLocalityGID } =
        await getZipcodeLocalityDebounced({
          country: country.value,
          zipcode: zipcodeValue,
        });

      clearMap();
      clearVenues();

      dispatch(setFormValue('locality', newLocality || ''));
      dispatch(setLocalityGID(newLocalityGID));

      const placeResponse = await getPlaces(newLocalityGID);
      const bounds = convertPlaceToBBox(placeResponse);

      centerMap(bounds);
    },
    [country, centerMap, clearMap, clearVenues]
  );

  const handleLocalityChange = useCallback((e, { value: localityValue }) => {
    dispatch(setFormValue('locality', localityValue));
  }, []);

  const handleAddressChange = useCallback(
    async (addressValue) => {
      dispatch(setFormValue('addressInputValue', ''));
      dispatch(setFormValue('address', addressValue));

      clearVenues();

      if (addressValue) {
        const placeResponse = await getPlaces(addressValue.value);
        const bounds = convertPlaceToBBox(placeResponse);
        const marker = convertPlaceToLatLngArray(placeResponse);

        centerMap(bounds);
        createMainMarker(marker);
        fetchVenues(marker);
      }
    },
    [centerMap, createMainMarker, fetchVenues, clearVenues]
  );

  const handleVenueChange = useCallback((venueValue) => {
    dispatch(setFormValue('venueInputValue', ''));
    dispatch(setFormValue('venue', venueValue));
  }, []);

  const handleAddressInputChange = useCallback(
    (inputCurrentValue, inputChangeInfo) => {
      _handleSelectChange({
        inputCurrentValue,
        inputChangeInfo,
        value: address,
        inputValue: addressInputValue,
        field: 'address',
        inputField: 'addressInputValue',
      });
    },
    [address, addressInputValue, _handleSelectChange]
  );

  const handleVenueInputChange = useCallback(
    (inputCurrentValue, inputChangeInfo) => {
      _handleSelectChange({
        inputCurrentValue,
        inputChangeInfo,
        value: venue,
        inputValue: venueInputValue,
        field: 'venue',
        inputField: 'venueInputValue',
      });
    },
    [venue, venueInputValue, _handleSelectChange]
  );

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
  }, []);

  const loadAddresses = useCallback(
    async (inputValue) => {
      const response = await autocompleteAddressesDebounced(inputValue, localityGID);

      return parseFeaturesForSelect(response.features);
    },
    [localityGID]
  );

  const contextValue = {
    country,
    zipcode,
    locality,

    address,
    addressInputValue,

    venue,
    venueInputValue,

    handleCountryChange,
    handleZipcodeChange,
    handleLocalityChange,

    handleAddressChange,
    handleAddressInputChange,

    handleVenueChange,
    handleVenueInputChange,

    handleSubmit,

    loadAddresses,
  };

  return (
    <POISearcherContext.Provider value={contextValue}>{props.children}</POISearcherContext.Provider>
  );
}

export { POISearcherContext, POISearcherContextProvider };
